import React, { createContext } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import { useIntl } from 'react-intl'
import { NamedRoutes } from './routes'
import { useHistory } from 'react-router-dom'
import SubscriptionServices from './Services/Consumers/SubscriptionServices'
import { ServicePayload } from './Services/Payloads/AuthenticatedUserPayload'

const ServiceContext = createContext({})

const ServiceProvider = ({ children, pageAction, sid, handleNotificationCall }) => {
  const { keycloak } = useKeycloak()
  const intl = useIntl()
  const history = useHistory()
  const [deleteServiceLoading, setDeleteServiceLoading] = React.useState(false)
  const [serviceDataLoading, setServiceDataLoading] = React.useState(false)
  const [serviceDataFailure, setServiceDataFailure] = React.useState(false)
  const [serviceData, setServiceData] = React.useState({})

  const [handlersList, setHandlersList] = React.useState([])
  const [handlersListLoading, setHandlersListLoading] = React.useState(false)

  const [categoriesList, setCategoriesList] = React.useState([])
  const [categoriesListLoading, setCategoriesListLoading] = React.useState(false)

  React.useEffect(() => {
      if ('delete' !== pageAction) {
        getHandlersList()
        getCategoriesList()
      }
      if (!!sid && !!parseInt(sid)) {
        getServiceData()
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const getHandlersList = () => {
    if (!!keycloak?.authenticated && !handlersList.length) {
      setHandlersListLoading(true)
      SubscriptionServices.getServiceHandlers({
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
        params: { 'page': 0, 'limit': 0 }
      }).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            setHandlersList(result.data.data)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        setHandlersListLoading(false)
      })
    }
  }

  const getCategoriesList = () => {
    if (!!keycloak?.authenticated && !categoriesList.length) {
      setCategoriesListLoading(true)
      SubscriptionServices.getCategories({
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
        params: { 'page': 0, 'limit': 0 }
      }).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            setCategoriesList(result.data.data)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        setCategoriesListLoading(false)
      })
    }
  }

  const getServiceData = () => {
    if (!!keycloak?.authenticated) {
      setServiceDataFailure(false)
      setServiceDataLoading(true)
      SubscriptionServices.getServiceData(sid, { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } }).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          setServiceDataFailure(true)
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            setServiceData(result.data.data)
          } else {
            setServiceDataFailure(true)
          }
        }
      }).then(() => {
        setServiceDataLoading(false)
      })
    }
  }

  const handleServiceSubmit = (values, FormikActions) => {
    if (!!keycloak?.authenticated) {
      FormikActions.setSubmitting(true)
      SubscriptionServices.submitService(sid, pageAction === 'edit', ServicePayload(values), {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }, FormikActions.setSubmitting, null, FormikActions).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            handleNotificationCall({
              type: 'done',
              message: intl.formatMessage({ id: pageAction === 'edit' ? 'Service.Edit.FormSuccessAlert' : 'Service.Add.FormSuccessAlert' }),
              color: 'success'
            }, 'success')
            history.push(NamedRoutes.subscription.service.list)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        FormikActions.setSubmitting(false)
      })
    }
  }

  const handleDeleteService = (index, data, setData, count, setCount, handleDeleteServiceDialog) => {
    if (!!keycloak?.authenticated) {
      setDeleteServiceLoading(true)
      SubscriptionServices.deleteService(data[index].id, {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }, setDeleteServiceLoading).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          handleNotificationCall({
            type: 'defence',
            message: result.response?.data?.status?.errors || intl.formatMessage({id: 'BasicInfo.FormErrorAlert'}),
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            handleNotificationCall({
              type: 'done',
              message: intl.formatMessage({ id: 'Service.Delete.FormSuccessAlert' }),
              color: 'success'
            }, 'success')
            let newData = data
            newData.splice(index, 1)
            setData(newData)
            setCount(--count)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        setDeleteServiceLoading(false)
        handleDeleteServiceDialog()
      })
    }
  }

  return (
    <ServiceContext.Provider value={{
      serviceDataLoading: serviceDataLoading,
      serviceDataFailure: serviceDataFailure,
      serviceData: serviceData,
      pageAction: pageAction,

      handlersListLoading: handlersListLoading,
      handlersList: handlersList,
      categoriesListLoading: categoriesListLoading,
      categoriesList: categoriesList,
      handleServiceSubmit,

      deleteServiceLoading: deleteServiceLoading,
      handleDeleteService
    }}>
      {children}
    </ServiceContext.Provider>
  )
}
const ServiceConsumer = ServiceContext.Consumer

export { ServiceProvider, ServiceConsumer }
